import React, { useState } from 'react';
import styled from 'styled-components';
import ShowingSchedule from '../../../components/ShowingSchedule';

const Form = styled.form`
  max-width: 900px;
  margin: 0 auto;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #f0f0f0;
`;

const SectionHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f8f9fa;
  
  h3 {
    margin: 0 0 4px 0;
    color: #333;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  &.single-column {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: #007AFF;
    color: white;
    border: none;
    
    &:hover {
      background: #0056b3;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: white;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background: #f5f5f5;
    }
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
`;

const EditPropertyForm = ({ property, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    // Property information
    address_line1: property?.address_line1 || '',
    unit_number: property?.unit_number || '',
    city: property?.city || '',
    state: property?.state || 'CA',
    zip_code: property?.zip_code || '',
    private_showing_instructions: property?.private_showing_instructions || '',
    
    // Scheduling availability
    availability_type: property?.availability_type || 'default',
    custom_availability: property?.custom_availability || {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: true, start: '09:00', end: '17:00' },
      sunday: { enabled: true, start: '09:00', end: '17:00' }
    },
    
    // Settings
    status: property?.status || 'listed',
    booking_approval: property?.booking_approval || 'automatic',
    buffer_time_minutes: property?.buffer_time_minutes || 15
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        full_address: formData.address_line1 + 
          (formData.unit_number ? ', ' + formData.unit_number : '') + 
          ', ' + formData.city + ', ' + formData.state + ' ' + formData.zip_code
      };
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error updating property:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Property Address Section */}
      <FormSection>
        <SectionHeader>
          <h3>🏠 Property Details</h3>
          <p>Update the property details and status.</p>
        </SectionHeader>
        
        <FormGrid>
          <FormField className="full-width">
            <Label>Street Address *</Label>
            <Input
              type="text"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              placeholder="123 Main Street"
              required
            />
          </FormField>
          
          <FormField>
            <Label>Unit Number</Label>
            <Input
              type="text"
              name="unit_number"
              value={formData.unit_number}
              onChange={handleChange}
              placeholder="Apt 2B, Unit 5, etc."
            />
          </FormField>
          
          <FormField>
            <Label>City *</Label>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="San Francisco"
              required
            />
          </FormField>
          
          <FormField>
            <Label>State *</Label>
            <Select
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            >
              <option value="CA">California</option>
            </Select>
          </FormField>
          
          <FormField>
            <Label>ZIP Code</Label>
            <Input
              type="text"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              placeholder="94102"
              pattern="[0-9]{5}(-[0-9]{4})?"
            />
          </FormField>
        </FormGrid>
      </FormSection>

      {/* Property Settings */}
      <FormSection>
        <SectionHeader>
          <h3>⚙️ Property Settings</h3>
          <p>Configure property status and booking settings.</p>
        </SectionHeader>
        
        <FormGrid>
          <FormField>
            <Label>Property Status</Label>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="listed">Listed - Accept Bookings</option>
              <option value="unlisted">Unlisted - No Bookings</option>
            </Select>
          </FormField>
          
          <FormField>
            <Label>Booking Approval</Label>
            <Select
              name="booking_approval"
              value={formData.booking_approval}
              onChange={handleChange}
            >
              <option value="automatic">Automatic - Instant Booking</option>
              <option value="manual">Manual - Require Approval</option>
            </Select>
          </FormField>
          
          <FormField>
            <Label>Buffer Time Between Showings</Label>
            <Select
              name="buffer_time_minutes"
              value={formData.buffer_time_minutes}
              onChange={handleChange}
            >
              <option value="0">No Buffer Time</option>
              <option value="15">15 Minutes</option>
              <option value="30">30 Minutes</option>
              <option value="60">1 Hour</option>
            </Select>
          </FormField>
        </FormGrid>
      </FormSection>

      {/* Showing Schedule */}
      <FormSection>
        <SectionHeader>
          <h3>📅 Showing Schedule</h3>
          <p>Set availability for property showings.</p>
        </SectionHeader>
        
        <ShowingSchedule
          schedule={formData.custom_availability}
          onChange={(newSchedule) => setFormData(prev => ({
            ...prev,
            custom_availability: newSchedule
          }))}
        />
      </FormSection>

      {/* Showing Instructions */}
      <FormSection>
        <SectionHeader>
          <h3>📋 Showing Instructions</h3>
          <p>Instructions for confirmed showings.</p>
        </SectionHeader>
        
        <FormField>
          <Label>Private Showing Instructions</Label>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
            🔒 Only shown to confirmed buyers and included in confirmation emails.
          </p>
          <TextArea
            name="private_showing_instructions"
            value={formData.private_showing_instructions}
            onChange={handleChange}
            placeholder="Example: 2 large dogs - will be secured in garage. Do not open garage. Door code 5555 if tenant not home. Lockbox on front gate, combo ABC123."
          />
        </FormField>
      </FormSection>

      <ButtonGroup>
        <Button 
          type="button" 
          className="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="primary"
          disabled={submitting}
        >
          {submitting ? 'Saving Changes...' : 'Save Changes'}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default EditPropertyForm;